import type { CookieOptions, CookieRef } from '#app';

type UseCookieOptions<T> = CookieOptions<T> & { readonly?: false };

/**
 * Cluster-scoped cookie helper. Every cookie name is prefixed with the
 * resolved cluster code — `us-cluster__auth`, `au-cluster__auth` — so tabs
 * for different clusters on the same apex never share cookies. The root
 * cluster is no exception: its cookies are also prefixed.
 *
 * The name prefix is the sole isolator; cookies are not scoped to the cluster's
 * path prefix. `useCookie` defaults the path to `/`, so a cluster's cookies are
 * readable across its whole path and never collide with another cluster's
 * (differently named) cookies on a shared apex.
 *
 * Cluster code is read from `useCurrentCluster()`, which on real requests
 * is populated by `~/router.options.ts` from the `X-Cluster-Code` header
 * Caddy injects. A missing code means the request didn't transit Caddy or
 * cluster setup hasn't run — throw rather than silently writing an
 * unscoped cookie that could leak between clusters.
 */
export const useClusterCookie = <T = string>(
  name: string,
  options?: UseCookieOptions<T>,
): CookieRef<T> => {
  const { code } = useCurrentCluster();
  if (!code.value) {
    throw new Error(
      `useClusterCookie('${name}') called without a resolved cluster code.`,
    );
  }
  return useCookie<T>(`${code.value}__${name}`, options);
};
